import {
  MedicalInfoAnswers,
  ReasonVisitAnswers,
  Screens,
  Thunk,
} from '../types';
import { selectPresentMeta, updateMeta } from '../meta';
import {
  EditPatientGuardiansInput,
  PatientGuardian,
  PatientInput,
  Precedence,
} from '../../generated/graphql';
import {
  updateAssessment,
  updateDemographics,
  updateEmergencyContact,
  updateGuardians,
  updateMedicationAndPharmacy,
  updatePrimaryPolicyholder,
} from '../assessment';
import { SpecialtyCareTypes } from '../../clinical/types';
import { setIsGuardian } from '../session';

// default to using a minor to maximize screen coverage
const patient: PatientInput = {
  phone: '1111111111',
  addressCity: 'New York',
  addressState: 'NY',
  addressStreet1: '109 W 27th St',
  addressStreet2: '',
  addressZipCode: '10001',
  email: 'vic.vinegar@alwayssunny.com',
  firstName: 'Vic',
  preferredName: 'Vic',
  lastName: 'Vinegar',
  middleName: '',
  birthDate: '2000-01-01',
  sex: 'Male',
};
const primaryGuardian: PatientGuardian = {
  phone: '2222222222',
  isAddressLinkedWithPatient: true,
  email: 'charlie.kelly@alwayssunny.com',
  firstName: 'Charlie',
  lastName: 'Kelly',
  birthDate: '1970-01-01',
  precedence: Precedence.Primary,
  relationToPatient: 'Father',
};
const secondaryGuardian: PatientGuardian = {
  phone: '2222222222',
  addressCity: 'Beverly Hills',
  addressState: 'CA',
  addressStreet1: '9641 Sunset Boulevard',
  addressStreet2: '',
  addressZipCode: '90210',
  isAddressLinkedWithPatient: false,
  email: 'bird@alwayssunny.com',
  firstName: 'Deandra',
  lastName: 'Reynolds',
  birthDate: '1969-01-01',
  precedence: Precedence.Secondary,
  relationToPatient: 'Mother',
};
const patientGuardians: EditPatientGuardiansInput = {
  primary: primaryGuardian,
  secondary: secondaryGuardian,
};
// TODO: Update here after v2 and v2.1 convergence
const reasonVisitAnswers: ReasonVisitAnswers = {
  [Screens.RV_MainReason]: ['Anxiety'],
  [Screens.RV_SecondaryReason]: ['No, none of these apply'],
  [Screens.RV_SpecialtyCare]: [SpecialtyCareTypes.None],
  [Screens.RV_PsychiatricImpatient]: ['No'],
  [Screens.RV_KillingYourself]: ['No'],
  [Screens.RV_DrinkingAlcohol]: ["I don't drink alcohol"],
  [Screens.RV_UseOfDrugs]: ['No, never'],
};
const medicalInfoAnswers: MedicalInfoAnswers = {
  MI_InsuranceProvider: ['Aetna'],
  MI_InsurancePlan: ['Activecare Plans_in-network'],
  MI_InsuranceId: ['test'],
  MI_PrimaryPolicyHolder: ['No'],
  MI_PrimaryPolicyHolderDetails: {
    firstName: 'Hugh',
    lastName: 'Honey',
    birthDate: '1989-01-01',
    addressStreet1: '1 oak rd',
    addressStreet2: '1',
    addressZipCode: '19019',
    addressCity: 'Philadelphia',
    addressState: 'PA',
    relationToPatient: 'Parent',
  },
  MI_PrescriptionMedication: ['Yes'],
  MI_MedicationList: [
    {
      label: 'ambien',
      value: 'ambien',
      __isNew__: true,
    },
  ],
  MI_OpenPsychiatricMeds: ['Yes'],
  MI_PrimaryPharmacy: ['Yes'],
  MI_PrimaryPharmacyDetails: {
    name: "Paddy's Pub",
    phone: '3333333333',
    addressStreet1: '1 oak rd',
    addressStreet2: '1',
    addressZipCode: '19019',
    addressCity: 'Philadelphia',
    addressState: 'PA',
  },
  [Screens.MI_EmergencyContact]: {
    firstName: 'Frank',
    lastName: 'Reynolds',
    phone: '4444444444',
    relation: 'Care Giver',
  },
  [Screens.MI_HaveAppointment]: ['No'],
};

const getInsuranceAnsInput = (medInfo: MedicalInfoAnswers) => {
  const [MI_InsuranceProvider, MI_InsurancePlan, MI_InsuranceId] = [
    'MI_InsuranceProvider',
    'MI_InsurancePlan',
    'MI_InsuranceId',
  ];
  return [
    {
      questionCode: MI_InsuranceProvider,
      answerValue: medInfo[MI_InsuranceProvider],
    },
    {
      questionCode: 'MI_InsurancePlan',
      answerValue: medInfo[MI_InsurancePlan],
    },
    {
      questionCode: 'MI_InsuranceId',
      answerValue: medInfo[MI_InsuranceId],
    },
  ];
};

export const devControlsUpdateRecursive =
  (selectedScreen: Screens, takeForMinor: boolean): Thunk =>
  async (dispatch, getState) => {
    const { screen } = selectPresentMeta(getState());
    if (screen === selectedScreen) return;

    // update according to what screen you're on
    if (screen === Screens.GetStarted) {
      dispatch(updateMeta());
    } else if (screen === Screens.PatientType) {
      dispatch(setIsGuardian(takeForMinor));
      dispatch(updateMeta());
    } else if (screen === Screens.GuardianDemographics) {
      await dispatch(updateGuardians(patientGuardians));
    } else if (screen === Screens.CollectDemographics) {
      const { email, ...updatedPatient } = patient;
      if (takeForMinor) updatedPatient.birthDate = '2010-01-01';
      await dispatch(updateDemographics(updatedPatient));
    } else if (screen.includes('RV_')) {
      const ansInput = [
        { questionCode: screen, answerValue: reasonVisitAnswers[screen] },
      ];
      await dispatch(updateAssessment(ansInput));
    } else if (screen === Screens.MI_InsuranceForm) {
      const ansInput = getInsuranceAnsInput(medicalInfoAnswers);
      await dispatch(updateAssessment(ansInput));
    } else if (screen === Screens.MI_InsuranceCombo) {
      const ansInput = getInsuranceAnsInput(medicalInfoAnswers);
      await dispatch(updateAssessment(ansInput, false));
      await dispatch(
        updatePrimaryPolicyholder(
          true,
          medicalInfoAnswers.MI_PrimaryPolicyHolderDetails
        )
      );
    } else if (screen === Screens.MI_EmergencyContact) {
      await dispatch(
        updateEmergencyContact(medicalInfoAnswers.MI_EmergencyContact)
      );
    } else if (screen === Screens.MI_MedicationPharmacyCombo) {
      const { MI_MedicationList, MI_PrimaryPharmacyDetails } =
        medicalInfoAnswers;
      await dispatch(
        updateMedicationAndPharmacy(
          true,
          MI_MedicationList,
          true,
          true,
          MI_PrimaryPharmacyDetails
        )
      );
    } else if (screen === Screens.MI_PrimaryPolicyHolder) {
      const isNotPolicyholder =
        medicalInfoAnswers.MI_PrimaryPolicyHolder.includes('No');
      await dispatch(
        updatePrimaryPolicyholder(
          isNotPolicyholder,
          medicalInfoAnswers.MI_PrimaryPolicyHolderDetails
        )
      );
    } else if (screen.includes('MI_')) {
      const ansInput = [
        { questionCode: screen, answerValue: medicalInfoAnswers[screen] },
      ];
      await dispatch(updateAssessment(ansInput));
    } else {
      dispatch(updateMeta());
    }

    return dispatch(devControlsUpdateRecursive(selectedScreen, takeForMinor));
  };
